/**
 * CompetitionPhaseService
 *
 * Manages the multi-window tip phases for CUP competitions (Champions League).
 * Phases are stored in the `competition_phases` DB table. The "effective" status
 * is derived client-side from timestamps and predecessor phase state, with the
 * DB status as a server-authoritative fallback/override.
 *
 * Phase lifecycle: pending → open → locked → resolved
 *
 * Note: special questions are managed by SpecialQuestionService and are not
 * part of this phase model.
 * This service only handles the phase metadata and active-window detection.
 */

import type {
  CompetitionPhase,
  CompetitionPhaseStatus,
  EffectivePhase,
} from "../types/index.ts";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

function supabaseHeaders() {
  return {
    apikey: ANON_KEY,
    Authorization: `Bearer ${ANON_KEY}`,
    "Content-Type": "application/json",
  };
}

class CompetitionPhaseService {
  /**
   * Fetch all phases for a competition, ordered by phase_order.
   */
  async listForCompetition(competitionId: number): Promise<CompetitionPhase[]> {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/competition_phases?competition_id=eq.${competitionId}&order=phase_order.asc`,
      { headers: supabaseHeaders() },
    );
    if (!res.ok) {
      console.error("CompetitionPhaseService.listForCompetition failed:", res.status);
      return [];
    }
    return res.json() as Promise<CompetitionPhase[]>;
  }

  /**
   * Resolve the effective status of a phase based on the current time and
   * its predecessor's state. The DB `status` field takes precedence when it
   * is 'resolved' (admin-set) or 'locked' (past deadline) — this handles
   * server-side manual overrides. Otherwise client-side time logic applies.
   */
  resolveEffectiveStatus(
    phase: CompetitionPhase,
    predecessorEffectiveStatus: CompetitionPhaseStatus | null,
  ): CompetitionPhaseStatus {
    const now = Date.now();

    // If DB says resolved, honour it
    if (phase.status === "resolved") return "resolved";

    // locked: DB says locked OR the locks_at timestamp has passed
    const pastDeadline = phase.locks_at != null && now >= new Date(phase.locks_at).getTime();
    if (pastDeadline || phase.status === "locked") return "locked";

    // Predecessor must be resolved/locked before this phase can open
    if (phase.unlocks_after_phase != null) {
      const predecessorDone =
        predecessorEffectiveStatus === "resolved" ||
        predecessorEffectiveStatus === "locked";
      if (!predecessorDone) return "pending";
    }

    // open: opens_at is set and in the past
    const opensNow = phase.opens_at != null && now >= new Date(phase.opens_at).getTime();
    if (opensNow) return "open";

    return "pending";
  }

  /**
   * Enrich phases with their effective status and isActive flag.
   * Call this after listForCompetition to get UI-ready phase data.
   */
  enrichPhases(phases: CompetitionPhase[]): EffectivePhase[] {
    // Build a map for quick predecessor lookup by phase_key
    const phaseMap = new Map(phases.map((p) => [p.phase_key, p]));

    let previousEffective: CompetitionPhaseStatus | null = null;
    const enriched: EffectivePhase[] = [];

    for (const phase of phases) {
      // Find predecessor's effective status
      let predEffective: CompetitionPhaseStatus | null = null;
      if (phase.unlocks_after_phase) {
        const predPhase = phaseMap.get(phase.unlocks_after_phase as import("../types/index.ts").CompetitionPhaseKey);
        if (predPhase) {
          const predEnriched = enriched.find((e) => e.phase_key === predPhase.phase_key);
          predEffective = predEnriched?.effectiveStatus ?? null;
        }
      } else {
        predEffective = previousEffective;
      }

      const effectiveStatus = this.resolveEffectiveStatus(phase, predEffective);
      enriched.push({
        ...phase,
        effectiveStatus,
        isActive: effectiveStatus === "open",
      });
      previousEffective = effectiveStatus;
    }

    return enriched;
  }

  /**
   * Fetch and enrich phases for a competition in one call.
   */
  async getEnrichedPhases(competitionId: number): Promise<EffectivePhase[]> {
    const phases = await this.listForCompetition(competitionId);
    return this.enrichPhases(phases);
  }

  /**
   * Returns the currently active (open) phase key, or null if none.
   */
  async getActivePhaseKey(competitionId: number): Promise<string | null> {
    const phases = await this.getEnrichedPhases(competitionId);
    return phases.find((p) => p.isActive)?.phase_key ?? null;
  }
}

export const competitionPhaseService = new CompetitionPhaseService();
